import DOMPurify from 'dompurify';

/**
 * Sanitizes note HTML before rendering.
 * Allows only safe tags and attributes, with strict style property restrictions.
 * Prevents XSS attacks while preserving Quill formatting (bold, italic, lists, links, colors, alignment).
 */
export function sanitizeNoteHtml(html: string): string {
  // Configure DOMPurify with strict allowlist
  const config = {
    ALLOWED_TAGS: ['p', 'br', 'span', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'blockquote', 'ol', 'ul', 'li', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style', 'data-list'],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|rel|data):|[^a-z]|[a-z+.-]*(?:[^a-z+.:-]|$))/i,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'svg', 'math', 'img', 'template'],
    FORBID_ATTR: ['srcdoc', 'formaction', 'xlink:href', 'srcset'],
    KEEP_CONTENT: true,
  };

  // Register a hook to restrict style properties
  // This hook runs after sanitization to ensure only safe CSS properties survive
  const restrictStyleProperties = (node) => {
    if (node.nodeType === 1 && node.hasAttribute('style')) {
      const styleAttr = node.getAttribute('style');
      if (styleAttr) {
        // Parse the style string and keep only allowed properties
        const allowedProperties = ['text-align', 'font-size', 'color', 'background-color'];
        const dangerousPatterns = ['url(', 'expression(', 'javascript', 'data:', 'vbscript:', '@import', 'behavior:'];

        // Check for dangerous patterns
        const hasDangerousPattern = dangerousPatterns.some((pattern) => styleAttr.toLowerCase().includes(pattern));
        if (hasDangerousPattern) {
          node.removeAttribute('style');
          return;
        }

        // Parse and filter style properties
        const styles = styleAttr.split(';').filter((s) => s.trim());
        const filteredStyles = styles
          .map((style) => {
            const [property, value] = style.split(':').map((s) => s.trim());
            if (property && allowedProperties.includes(property.toLowerCase())) {
              return `${property}: ${value}`;
            }
            return null;
          })
          .filter((s) => s !== null);

        if (filteredStyles.length > 0) {
          node.setAttribute('style', filteredStyles.join('; '));
        } else {
          node.removeAttribute('style');
        }
      }
    }
  };

  // Register the hook globally
  DOMPurify.addHook('afterSanitizeAttributes', restrictStyleProperties);

  try {
    // Sanitize the HTML
    const sanitized = DOMPurify.sanitize(html, config);
    return sanitized;
  } finally {
    // Remove the hook to prevent it from affecting other callers
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
}
